import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import useCurrentUser from "hooks/useCurrentUser";
import {
  PageContainer, StatCard, PremiumCard, DataTable, LoadingState,
} from "components/ui";

const IconCoins = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
  </svg>
);
const IconCard = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" />
  </svg>
);
const IconCollection = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
  </svg>
);
const IconChart = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const AccountantDashboard = () => {
  const axiosPrivate = useAxiosPrivate();
  const { user } = useCurrentUser();

  const [trialBalance, setTrialBalance] = useState([]);
  const [income, setIncome] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const year = new Date().getFullYear();
      try {
        const [tbRes, incRes] = await Promise.all([
          axiosPrivate.get("/api/accountant/trial-balance"),
          axiosPrivate.get(`/api/accountant/income-statement?start=${year}-01-01&end=${year}-12-31`),
        ]);
        setTrialBalance(tbRes.data);
        setIncome(incRes.data);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchData();
  }, [user]);

  const totalAssets = trialBalance
    .filter((a) => a.accountType === "ASSET")
    .reduce((sum, a) => sum + Number(a.debitSum) - Number(a.creditSum), 0);
  const totalLiabilities = trialBalance
    .filter((a) => a.accountType === "LIABILITY")
    .reduce((sum, a) => sum + Number(a.creditSum) - Number(a.debitSum), 0);

  if (loading) {
    return (
      <PageContainer>
        <PremiumCard><LoadingState label="Loading books…" /></PremiumCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <StatCard label="Total assets" value={`${totalAssets.toFixed(2)} TND`} icon={<IconCoins />} />
        <StatCard
          label="Liabilities" value={`${totalLiabilities.toFixed(2)} TND`}
          icon={<IconCard />}
          accent={{ bg: "var(--danger-bg)", fg: "var(--danger)" }}
        />
        <StatCard
          label="Revenue" value={`${income ? Number(income.revenue).toFixed(2) : "0.00"} TND`}
          icon={<IconCollection />}
          accent={{ bg: "var(--success-bg)", fg: "var(--success)" }}
        />
        <StatCard
          label="Net income" value={`${income ? Number(income.netIncome).toFixed(2) : "0.00"} TND`}
          icon={<IconChart />}
          accent={{ bg: "var(--warning-bg)", fg: "var(--warning)" }}
        />
      </div>

      <DataTable
        title="Trial balance"
        subtitle="Account-level summary"
        data={trialBalance}
        rowKey={(a) => a.accountCode}
        search={true}
        searchPlaceholder="Search accounts…"
        pageSize={10}
        emptyTitle="No accounts"
        columns={[
          {
            key: "accountName", header: "Account",
            render: (a) => (
              <div>
                <div style={{ fontWeight: 600 }}>{a.accountName}</div>
                <div className="t-mono t-muted" style={{ fontSize: 12 }}>{a.accountCode}</div>
              </div>
            ),
          },
          { key: "accountType", header: "Type", render: (a) => <span className="t-body-xs">{a.accountType}</span> },
          { key: "debitSum",  header: "Debit",  align: "right", render: (a) => <span className="t-mono">{Number(a.debitSum).toFixed(2)}</span> },
          { key: "creditSum", header: "Credit", align: "right", render: (a) => <span className="t-mono">{Number(a.creditSum).toFixed(2)}</span> },
        ]}
      />
    </PageContainer>
  );
};

export default AccountantDashboard;